import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClipboardList, Send, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuestionnairePage() {
  const { qid } = useParams();
  const [qst, setQst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [responses, setResponses] = useState({});
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/questionnaire/${qid}`).then(r => { setQst(r.data); setLoading(false); }).catch(() => { setError("Formulaire introuvable ou inactif."); setLoading(false); });
  }, [qid]);

  const isFieldVisible = (fieldId) => {
    const conditions = (qst?.conditions || []).filter(c => (c.then_show_fields || []).includes(fieldId));
    if (conditions.length === 0) return true;
    return conditions.some(c => {
      const val = responses[c.if_field_id] || "";
      if (c.operator === "equals") return val === c.value;
      if (c.operator === "not_equals") return val !== c.value;
      if (c.operator === "contains") return val.includes(c.value);
      if (c.operator === "not_empty") return val.length > 0;
      return true;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/questionnaire/${qid}/submit`, { responses, contact });
      setSubmitted(true);
    } catch (e) { alert("Erreur lors de la soumission."); }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#0f0a1a] text-white flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>;
  if (error) return <div className="min-h-screen bg-[#0f0a1a] text-white flex items-center justify-center"><p className="text-red-400">{error}</p></div>;
  if (submitted) return (
    <div className="min-h-screen bg-[#0f0a1a] text-white flex items-center justify-center">
      <div className="text-center max-w-md">
        <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-400" />
        <h1 className="text-2xl font-bold mb-2">Merci !</h1>
        <p className="text-slate-400">Votre formulaire a ete soumis avec succes. Nous vous recontacterons bientot.</p>
      </div>
    </div>
  );

  const sections = {};
  (qst?.fields || []).forEach(f => { const sec = f.section || "General"; if (!sections[sec]) sections[sec] = []; sections[sec].push(f); });

  return (
    <div className="min-h-screen bg-[#0f0a1a] text-white" data-testid="public-questionnaire">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <ClipboardList className="w-10 h-10 mx-auto mb-3" style={{ color: "#f7c935" }} />
          <h1 className="text-3xl font-black">{qst?.name}</h1>
          {qst?.description && <p className="text-slate-400 mt-2">{qst.description}</p>}
        </div>

        {/* Contact info */}
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 mb-6">
          <h3 className="text-sm font-bold mb-3" style={{ color: "#f7c935" }}>Vos coordonnees</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Prenom *" value={contact.first_name} onChange={e => setContact(c => ({ ...c, first_name: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
            <Input placeholder="Nom *" value={contact.last_name} onChange={e => setContact(c => ({ ...c, last_name: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
            <Input placeholder="Email *" value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
            <Input placeholder="Telephone" value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
            <Input placeholder="Entreprise" value={contact.company} onChange={e => setContact(c => ({ ...c, company: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white col-span-2" />
          </div>
        </div>

        {/* Questionnaire fields */}
        {Object.entries(sections).map(([secName, secFields]) => (
          <div key={secName} className="bg-[#14101c] border border-white/10 rounded-xl p-6 mb-4">
            <h3 className="text-sm font-bold mb-4 text-slate-300 uppercase tracking-wide">{secName}</h3>
            <div className="space-y-4">
              {secFields.map(f => {
                const fid = f.field_id || f.id;
                if (!isFieldVisible(fid)) return null;
                return (
                  <div key={fid}>
                    <label className="text-sm text-slate-400 mb-1.5 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                    {(f.type === "text" || f.type === "email" || f.type === "phone" || f.type === "number") && (
                      <Input type={f.type === "number" ? "number" : "text"} value={responses[fid] || ""} onChange={e => setResponses(r => ({ ...r, [fid]: e.target.value }))} placeholder={f.placeholder || ""} className="bg-[#0b0811] border-white/10 text-white" />
                    )}
                    {f.type === "textarea" && (
                      <Textarea value={responses[fid] || ""} onChange={e => setResponses(r => ({ ...r, [fid]: e.target.value }))} placeholder={f.placeholder || ""} rows={3} className="bg-[#0b0811] border-white/10 text-white" />
                    )}
                    {f.type === "select" && (
                      <Select value={responses[fid] || ""} onValueChange={v => setResponses(r => ({ ...r, [fid]: v }))}>
                        <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                        <SelectContent className="bg-[#14101c] border-white/10 text-white">
                          {(f.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {f.type === "checkbox" && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {(f.options || []).map(opt => {
                          const selected = (responses[fid] || "").split(",").map(s => s.trim()).includes(opt);
                          return (
                            <button key={opt} onClick={() => {
                              const cur = (responses[fid] || "").split(",").map(s => s.trim()).filter(Boolean);
                              const next = selected ? cur.filter(x => x !== opt) : [...cur, opt];
                              setResponses(r => ({ ...r, [fid]: next.join(", ") }));
                            }}
                              className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${selected ? "bg-[#8b5cf6]/10 text-[#8b5cf6] border-[#8b5cf6]/30" : "bg-white/5 text-slate-500 border-white/5 hover:border-white/10"}`}>{opt}</button>
                          );
                        })}
                      </div>
                    )}
                    {f.type === "date" && (
                      <Input type="date" value={responses[fid] || ""} onChange={e => setResponses(r => ({ ...r, [fid]: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <Button onClick={handleSubmit} disabled={submitting || !contact.first_name || !contact.email}
          className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold py-3 text-lg" data-testid="public-submit-btn">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Envoi...</> : <><Send className="w-5 h-5 mr-2" /> Envoyer</>}
        </Button>
      </div>
    </div>
  );
}
